import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Clock, Plus, Calendar, DollarSign, Filter } from 'lucide-react';

interface WorkHour {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  break_minutes: number;
  task_description: string;
  project_code: string;
  total_hours: number;
  hourly_rate_chf: number;
  gross_pay_chf: number;
  status: string;
  paid_status: string;
  created_at: string;
}

const EmployeeHours = () => {
  const { user, loading, userRole } = useAuth();
  const { toast } = useToast();
  const [workHours, setWorkHours] = useState<WorkHour[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<WorkHour | null>(null);
  const [userHourlyRate, setUserHourlyRate] = useState<number>(25);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '17:00',
    break_minutes: 60,
    task_description: '',
    project_code: ''
  });

  const [calculatedHours, setCalculatedHours] = useState(0);
  const [calculatedPay, setCalculatedPay] = useState(0);

  useEffect(() => {
    if (user) {
      fetchWorkHours();
      fetchUserProfile();
    }
  }, [user]);

  // Calculate hours and pay in real time
  useEffect(() => {
    if (formData.start_time && formData.end_time) {
      const start = new Date(`2000-01-01T${formData.start_time}:00`);
      const end = new Date(`2000-01-01T${formData.end_time}:00`);
      const totalMinutes = (end.getTime() - start.getTime()) / (1000 * 60) - formData.break_minutes;
      const hours = Math.max(0, Math.round((totalMinutes / 60) * 4) / 4); // Round to nearest 0.25h
      
      setCalculatedHours(hours);
      
      // Calculate pay based on user's hourly rate
      setCalculatedPay(hours * userHourlyRate);
    }
  }, [formData.start_time, formData.end_time, formData.break_minutes, userHourlyRate]);

  // Redirect if not authenticated
  if (!loading && !user) {
    return <Navigate to="/auth" replace />;
  }

  // Redirect admins to admin dashboard
  if (!loading && user && (userRole === 'ADMIN' || userRole === 'admin')) {
    return <Navigate to="/admin" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-je-red mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const fetchWorkHours = async () => {
    try {
      const { data, error } = await supabase
        .from('work_hours')
        .select('*')
        .eq('user_id', user!.id)
        .order('date', { ascending: false });

      if (error) throw error;
      setWorkHours(data || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch work hours"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('hourly_rate_chf')
        .eq('user_id', user!.id)
        .single();

      if (error) throw error;
      if (data) {
        setUserHourlyRate(data.hourly_rate_chf || 25);
      }
    } catch (error: any) {
      console.error('Error fetching user profile:', error);
      // Keep default rate if no profile found
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      start_time: '09:00',
      end_time: '17:00',
      break_minutes: 60,
      task_description: '',
      project_code: ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const entryData = {
        user_id: user!.id,
        date: formData.date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        break_minutes: formData.break_minutes,
        task_description: formData.task_description,
        project_code: formData.project_code
      };

      let error;
      if (editingEntry) {
        ({ error } = await supabase
          .from('work_hours')
          .update(entryData)
          .eq('id', editingEntry.id));
      } else {
        ({ error } = await supabase
          .from('work_hours')
          .insert([entryData]));
      }

      if (error) throw error;

      toast({
        title: "Succès",
        description: editingEntry ? "Entrée de travail mise à jour" : "Entrée de travail ajoutée"
      });

      setShowForm(false);
      setEditingEntry(null);
      resetForm();
      fetchWorkHours();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    }
  };

  const handleEdit = (entry: WorkHour) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      start_time: entry.start_time,
      end_time: entry.end_time,
      break_minutes: entry.break_minutes,
      task_description: entry.task_description || '',
      project_code: entry.project_code || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (entryId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette entrée ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('work_hours')
        .delete()
        .eq('id', entryId);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Entrée supprimée"
      });

      fetchWorkHours();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    }
  };


  const totalHoursThisMonth = workHours
    .filter(entry => {
      const entryDate = new Date(entry.date);
      const now = new Date();
      return entryDate.getMonth() === now.getMonth() && entryDate.getFullYear() === now.getFullYear();
    })
    .reduce((sum, entry) => sum + parseFloat(entry.total_hours.toString()), 0);

  const pendingHoursThisMonth = workHours
    .filter(entry => {
      const entryDate = new Date(entry.date);
      const now = new Date();
      return entryDate.getMonth() === now.getMonth() && 
             entryDate.getFullYear() === now.getFullYear() && 
             entry.status === 'submitted';
    })
    .reduce((sum, entry) => sum + parseFloat(entry.total_hours.toString()), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">JE-HEPIA</h1>
              <div className="ml-6">
                <span className="text-sm text-gray-500">Gestion des Heures</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                Bienvenue, {user?.email}
              </span>
              <Button 
                variant="outline" 
                onClick={() => supabase.auth.signOut()}
              >
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="flex items-center p-6">
              <Clock className="h-8 w-8 text-je-red mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Heures ce mois</p>
                <p className="text-2xl font-bold text-gray-900">{totalHoursThisMonth.toFixed(2)}h</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <Clock className="h-8 w-8 text-je-red mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Heures en attente</p>
                <p className="text-2xl font-bold text-gray-900">{pendingHoursThisMonth.toFixed(2)}h</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div className="flex items-center">
                <DollarSign className="h-8 w-8 text-je-red mr-4" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Taux horaire</p>
                  <p className="text-2xl font-bold text-gray-900">{userHourlyRate} CHF/h</p>
                  <p className="text-xs text-gray-500 mt-1">Défini par l'administration</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Add Entry Form */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>{editingEntry ? "Modifier l'entrée" : "Ajouter des heures"}</CardTitle>
                <CardDescription>
                  {editingEntry ? "Modifier votre entrée de travail" : "Enregistrez vos heures de travail quotidiennes"}
                </CardDescription>
              </div>
              <Button
                onClick={() => {
                  setShowForm(!showForm);
                  if (!showForm) {
                    setEditingEntry(null);
                    resetForm();
                  }
                }}
                className="bg-je-red hover:bg-red-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                {showForm ? 'Annuler' : 'Ajouter'}
              </Button>
            </div>
          </CardHeader>

          {showForm && (
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="start_time">Heure de début</Label>
                  <Input
                    id="start_time"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="end_time">Heure de fin</Label>
                  <Input
                    id="end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="break_minutes">Pause (minutes)</Label>
                  <Input
                    id="break_minutes"
                    type="number"
                    min="0"
                    value={formData.break_minutes}
                    onChange={(e) => setFormData({ ...formData, break_minutes: parseInt(e.target.value) || 0 })}
                  />
                </div>

                <div>
                  <Label htmlFor="project_code">Code de projet</Label>
                  <Input
                    id="project_code"
                    placeholder="Optionnel"
                    value={formData.project_code}
                    onChange={(e) => setFormData({ ...formData, project_code: e.target.value })}
                  />
                </div>

                <div className="md:col-span-2 lg:col-span-1">
                  <Label>Calculé</Label>
                  <div className="text-sm text-gray-600 mt-1">
                    <div>{calculatedHours.toFixed(2)}h • {calculatedPay.toFixed(2)} CHF</div>
                  </div>
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <Label htmlFor="task_description">Description de la tâche</Label>
                  <Textarea
                    id="task_description"
                    placeholder="Décrivez sur quoi vous avez travaillé..."
                    value={formData.task_description}
                    onChange={(e) => setFormData({ ...formData, task_description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <Button type="submit" className="bg-je-red hover:bg-red-700">
                    {editingEntry ? 'Modifier l\'entrée' : 'Ajouter l\'entrée'}
                  </Button>
                </div>
              </form>
            </CardContent>
          )}
        </Card>

        {/* Work Hours List */}
        <Card>
          <CardHeader>
            <CardTitle>Vos heures de travail</CardTitle>
            <CardDescription>Consultez et gérez vos entrées de temps</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">Chargement...</div>
            ) : workHours.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Aucune heure de travail enregistrée. Ajoutez votre première entrée ci-dessus !
              </div>
            ) : (
              <div className="space-y-4">
                {workHours.map((entry) => (
                  <div key={entry.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-2">
                          <span className="font-medium">{entry.date}</span>
                          <span className="text-gray-600">
                            {entry.start_time} - {entry.end_time}
                          </span>
                        </div>
                         <div className="text-sm text-gray-600 mb-2">
                           <span className="font-medium">{entry.total_hours.toFixed(2)}h de travail</span>
                           {entry.break_minutes > 0 && ` • Pause: ${entry.break_minutes}min`}
                           {entry.project_code && ` • Projet: ${entry.project_code}`}
                           <span className="ml-2 text-je-red font-medium">• {entry.gross_pay_chf.toFixed(2)} CHF</span>
                         </div>
                        {entry.task_description && (
                          <p className="text-sm text-gray-700">{entry.task_description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(entry)}
                        >
                          Modifier
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(entry.id)}
                        >
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmployeeHours;